import React, { useState, useEffect, useCallback, useMemo } from 'react';
import WorkingRegimeForm from './WorkingRegimeForm';
import {
    Briefcase, PlusCircle, Edit, Trash, Loader2, AlertCircle, CheckCircle,
    Clock, Users, RefreshCw, ArrowRightLeft, AlertTriangle, Calendar, CheckCircle2
} from 'lucide-react';
import { apiClient } from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';
import DataTable from './common/DataTable';
import InfoModal from './common/InfoModal';

const DAY_BADGES = [
    { key: 'mondayShiftGroupName', label: 'Pzt' },
    { key: 'tuesdayShiftGroupName', label: 'Sal' },
    { key: 'wednesdayShiftGroupName', label: 'Çar' },
    { key: 'thursdayShiftGroupName', label: 'Per' },
    { key: 'fridayShiftGroupName', label: 'Cum' },
    { key: 'saturdayShiftGroupName', label: 'Cmt' },
    { key: 'sundayShiftGroupName', label: 'Paz' },
];

export default function WorkingRegimeManagement() {
    const [workingRegimeList, setWorkingRegimeList] = useState([]);
    const [workPeriods, setWorkPeriods] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedWorkingRegime, setSelectedWorkingRegime] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);

    // Basit silme onayı (0 personelli rejimler için)
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [workingRegimeToDelete, setWorkingRegimeToDelete] = useState(null);

    // Personel Aktarım / Güvenli Silme Modalı
    const [transferModal, setTransferModal] = useState({
        isOpen: false,
        mode: 'transfer', // 'transfer' | 'delete'
        sourceRegime: null,
        targetRegimeId: '',
        syncSchedulesToTargetRegime: true,
        scheduleEffectiveDate: new Date().toISOString().split('T')[0],
    });

    // Vardiyaları Eşitle (Sync) Modalı
    const [syncModal, setSyncModal] = useState({
        isOpen: false,
        regime: null,
        preserveCustomEmployeeSchedules: false,
        scheduleEffectiveDate: new Date().toISOString().split('T')[0],
    });

    const { isAuthenticated, hasPermission } = useAuth();

    const canView = true;
    const canCreate = !hasPermission || hasPermission('working_regimes:create') || hasPermission('departments:create') || hasPermission('work_periods:create');
    const canUpdate = !hasPermission || hasPermission('working_regimes:update') || hasPermission('departments:update') || hasPermission('work_periods:update');
    const canDelete = !hasPermission || hasPermission('working_regimes:delete') || hasPermission('departments:delete') || hasPermission('work_periods:delete');

    const fetchAllData = useCallback(async () => {
        if (!canView) return;
        setLoading(true);
        setError(null);
        try {
            const [regimeRes, periodRes] = await Promise.allSettled([
                apiClient('WorkingRegime'),
                apiClient('scheduling/periods'),
            ]);

            if (regimeRes.status === 'fulfilled') {
                setWorkingRegimeList(Array.isArray(regimeRes.value) ? regimeRes.value : []);
            } else {
                throw regimeRes.reason;
            }

            if (periodRes.status === 'fulfilled') {
                setWorkPeriods(Array.isArray(periodRes.value) ? periodRes.value : []);
            }
        } catch (err) {
            console.error("Çalışma rejimleri çekilirken hata oluştu:", err);
            setError(err.message || "Çalışma rejimlerini getirirken bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    }, [canView]);

    useEffect(() => {
        if (isAuthenticated) {
            fetchAllData();
        }
    }, [isAuthenticated, fetchAllData]);

    const stats = useMemo(() => {
        const total = workingRegimeList.length;
        const activeRegimes = workingRegimeList.filter(r => r.isActive).length;
        const linkedToPeriod = workingRegimeList.filter(r => r.defaultWorkPeriodId).length;
        const totalActiveEmployees = workingRegimeList.reduce((acc, r) => acc + (r.activeEmployeeCount || 0), 0);
        const totalDiffSchedules = workingRegimeList.reduce((acc, r) => acc + (r.differentOrMissingScheduleCount || 0), 0);
        return { total, activeRegimes, linkedToPeriod, totalActiveEmployees, totalDiffSchedules };
    }, [workingRegimeList]);

    const handleAddClick = () => {
        setSelectedWorkingRegime(null);
        setIsModalOpen(true);
        setMessage(null);
        setError(null);
    };

    const handleEditClick = (workingRegime) => {
        setSelectedWorkingRegime(workingRegime);
        setIsModalOpen(true);
        setMessage(null);
        setError(null);
    };

    const openTransferOrDeleteModal = (regime, mode = 'transfer') => {
        const otherActiveRegimes = workingRegimeList.filter(r => r.id !== regime.id && r.isActive);
        setTransferModal({
            isOpen: true,
            mode,
            sourceRegime: regime,
            targetRegimeId: otherActiveRegimes.length > 0 ? String(otherActiveRegimes[0].id) : '',
            syncSchedulesToTargetRegime: true,
            scheduleEffectiveDate: new Date().toISOString().split('T')[0],
        });
    };

    const handleDeleteRequest = (regime) => {
        setMessage(null);
        setError(null);
        if ((regime.totalEmployeeCount || 0) > 0) {
            openTransferOrDeleteModal(regime, 'delete');
        } else {
            setWorkingRegimeToDelete(regime);
            setIsConfirmModalOpen(true);
        }
    };

    const handleDeleteConfirm = async () => {
        setIsConfirmModalOpen(false);
        if (!workingRegimeToDelete) return;
        setLoading(true);
        setError(null);
        try {
            await apiClient(`WorkingRegime/${workingRegimeToDelete.id}`, { method: 'DELETE' });
            setMessage(`'${workingRegimeToDelete.name}' çalışma rejimi başarıyla silindi.`);
            fetchAllData();
        } catch (err) {
            if (err.data?.requiresReassignment) {
                openTransferOrDeleteModal(workingRegimeToDelete, 'delete');
            } else {
                setError(err.message || "Çalışma rejimi silinirken bir hata oluştu.");
            }
        } finally {
            setLoading(false);
            setWorkingRegimeToDelete(null);
        }
    };

    const handleTransferConfirm = async (e) => {
        e.preventDefault();
        const { sourceRegime, targetRegimeId, mode, syncSchedulesToTargetRegime, scheduleEffectiveDate } = transferModal;
        if (!sourceRegime || !targetRegimeId) {
            setError("Lütfen personellerin aktarılacağı hedef çalışma rejimini seçin.");
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const endpoint = mode === 'delete'
                ? `WorkingRegime/${sourceRegime.id}/reassign-and-delete`
                : `WorkingRegime/${sourceRegime.id}/transfer-employees`;

            const res = await apiClient(endpoint, {
                method: 'POST',
                body: {
                    targetWorkingRegimeId: parseInt(targetRegimeId, 10),
                    syncSchedulesToTargetRegime,
                    scheduleEffectiveDate: syncSchedulesToTargetRegime && scheduleEffectiveDate ? scheduleEffectiveDate : null,
                },
            });

            setMessage(res?.message || "İşlem başarıyla tamamlandı.");
            setTransferModal(prev => ({ ...prev, isOpen: false, sourceRegime: null }));
            fetchAllData();
        } catch (err) {
            setError(err.message || "Personel aktarımı sırasında bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    };

    const openSyncModal = (regime) => {
        setMessage(null);
        setError(null);
        setSyncModal({
            isOpen: true,
            regime,
            preserveCustomEmployeeSchedules: false,
            scheduleEffectiveDate: new Date().toISOString().split('T')[0],
        });
    };

    const handleSyncConfirm = async (e) => {
        e.preventDefault();
        if (!syncModal.regime) return;

        setLoading(true);
        setError(null);
        try {
            const res = await apiClient(`WorkingRegime/${syncModal.regime.id}/sync-schedules`, {
                method: 'POST',
                body: {
                    preserveCustomEmployeeSchedules: syncModal.preserveCustomEmployeeSchedules,
                    scheduleEffectiveDate: syncModal.scheduleEffectiveDate || null,
                },
            });

            setMessage(res?.message || "Vardiya senkronizasyonu tamamlandı.");
            setSyncModal(prev => ({ ...prev, isOpen: false, regime: null }));
            fetchAllData();
        } catch (err) {
            setError(err.message || "Vardiyalar eşitlenirken bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    };

    const handleFormSubmit = async (workingRegimeData) => {
        setLoading(true);
        setError(null);
        try {
            const method = workingRegimeData.id ? 'PUT' : 'POST';
            const endpoint = workingRegimeData.id ? `WorkingRegime/${workingRegimeData.id}` : 'WorkingRegime';
            const res = await apiClient(endpoint, {
                method,
                body: workingRegimeData,
            });
            setMessage(res?.message || `Çalışma rejimi başarıyla ${workingRegimeData.id ? 'güncellendi' : 'eklendi'}!`);
            setIsModalOpen(false);
            setSelectedWorkingRegime(null);
            fetchAllData();
        } catch (err) {
            if (err.data?.requiresReassignment && selectedWorkingRegime) {
                setIsModalOpen(false);
                openTransferOrDeleteModal(selectedWorkingRegime, 'transfer');
                setError(err.message);
            } else {
                setError(err.message || "Çalışma rejimi işlemi sırasında beklenmedik bir hata oluştu.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setIsConfirmModalOpen(false);
        setSelectedWorkingRegime(null);
        setWorkingRegimeToDelete(null);
        setError(null);
    };

    const workingRegimeColumns = useMemo(() => {
        const columns = [
            {
                header: 'Çalışma Rejimi',
                accessor: 'name',
                sortable: true,
                searchable: true,
                render: (row) => (
                    <div className="flex flex-col">
                        <span className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
                            <Briefcase className="w-4 h-4 text-indigo-500 shrink-0" />
                            {row.name}
                        </span>
                        {row.description && (
                            <span className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">
                                {row.description}
                            </span>
                        )}
                    </div>
                ),
            },
            {
                header: 'Bağlı Çalışma Periyodu (Otomatik Vardiya)',
                accessor: 'defaultWorkPeriodName',
                sortable: true,
                searchable: true,
                render: (row) => {
                    if (!row.defaultWorkPeriodId) {
                        return (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
                                Otomatik Periyot Yok (Manuel)
                            </span>
                        );
                    }
                    return (
                        <div className="space-y-1.5">
                            <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-xs font-semibold bg-indigo-50 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-700/60">
                                    <Clock className="w-3.5 h-3.5" />
                                    {row.defaultWorkPeriodName}
                                </span>
                                {row.hasDynamicDayOff && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 font-medium">
                                        Dinamik {row.dynamicDayOffCount || 1}G Tatil
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-1 flex-wrap">
                                {DAY_BADGES.map(d => {
                                    const sgName = row[d.key];
                                    return (
                                        <span
                                            key={d.key}
                                            title={`${d.label}: ${sgName || 'Tatil'}`}
                                            className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                                                sgName
                                                    ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/50'
                                                    : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500'
                                            }`}
                                        >
                                            {d.label}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    );
                },
            },
            {
                header: 'Kayıtlı Personel & Vardiya Uyumu',
                accessor: 'activeEmployeeCount',
                sortable: true,
                searchable: false,
                render: (row) => {
                    const active = row.activeEmployeeCount || 0;
                    const inactive = row.inactiveEmployeeCount || 0;
                    const matching = row.matchingScheduleEmployeeCount || 0;
                    const diff = row.differentOrMissingScheduleCount || 0;

                    return (
                        <div className="space-y-1">
                            <div className="flex items-center gap-2">
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                    <Users className="w-3.5 h-3.5" />
                                    {active} Aktif
                                </span>
                                {inactive > 0 && (
                                    <span className="text-xs text-gray-400 dark:text-gray-500">
                                        (+{inactive} Pasif)
                                    </span>
                                )}
                            </div>
                            {row.defaultWorkPeriodId && active > 0 && (
                                <div className="flex items-center gap-1.5 text-[11px]">
                                    <span className="text-emerald-600 dark:text-emerald-400 font-medium">
                                        {matching} Uyumlu
                                    </span>
                                    {diff > 0 && (
                                        <span
                                            className="px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 font-semibold"
                                            title="Bu rejimde olup şu anki vardiyası farklı/geçici olan veya henüz vardiya atanmamış personel sayısı"
                                        >
                                            {diff} Farklı/Atanmamış
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                },
            },
            {
                header: 'Durum',
                accessor: 'isActive',
                sortable: true,
                searchable: false,
                render: (row) => (
                    <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                            row.isActive
                                ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300'
                                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                        }`}
                    >
                        {row.isActive ? 'Aktif' : 'Pasif'}
                    </span>
                ),
            },
        ];

        if (canUpdate || canDelete) {
            columns.push({
                header: 'Aksiyonlar',
                accessor: 'actions',
                render: (row) => (
                    <div className="flex items-center space-x-1.5">
                        {canUpdate && row.defaultWorkPeriodId && (row.activeEmployeeCount || 0) > 0 && (
                            <button
                                onClick={() => openSyncModal(row)}
                                className="p-1.5 rounded-lg text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 transition-colors"
                                title="Bu rejimdeki personellerin vardiyalarını rejimin periyoduna eşitle"
                            >
                                <RefreshCw className="w-4 h-4" />
                            </button>
                        )}
                        {canUpdate && (row.totalEmployeeCount || 0) > 0 && workingRegimeList.filter(r => r.id !== row.id && r.isActive).length > 0 && (
                            <button
                                onClick={() => openTransferOrDeleteModal(row, 'transfer')}
                                className="p-1.5 rounded-lg text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30 transition-colors"
                                title="Personelleri başka bir çalışma rejimine aktar"
                            >
                                <ArrowRightLeft className="w-4 h-4" />
                            </button>
                        )}
                        {canUpdate && (
                            <button
                                onClick={() => handleEditClick(row)}
                                className="p-1.5 rounded-lg text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors"
                                title="Düzenle"
                            >
                                <Edit className="w-4 h-4" />
                            </button>
                        )}
                        {canDelete && (
                            <button
                                onClick={() => handleDeleteRequest(row)}
                                className="p-1.5 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                                title="Sil"
                            >
                                <Trash className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                ),
            });
        }
        return columns;
    }, [canUpdate, canDelete, workingRegimeList]);

    const otherActiveRegimesForTransfer = useMemo(() => {
        if (!transferModal.sourceRegime) return [];
        return workingRegimeList.filter(r => r.id !== transferModal.sourceRegime.id && r.isActive);
    }, [workingRegimeList, transferModal.sourceRegime]);

    return (
        <div className="p-1 space-y-4">
            {/* Özet KPI Kartları */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="p-3.5 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Toplam Çalışma Rejimi</p>
                        <p className="text-xl font-bold text-gray-900 dark:text-white mt-0.5">
                            {stats.total} <span className="text-xs font-normal text-emerald-600 dark:text-emerald-400">({stats.activeRegimes} Aktif)</span>
                        </p>
                    </div>
                    <div className="p-2.5 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400">
                        <Briefcase className="w-5 h-5" />
                    </div>
                </div>

                <div className="p-3.5 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Otomatik Vardiya Bağlı</p>
                        <p className="text-xl font-bold text-indigo-600 dark:text-indigo-400 mt-0.5">
                            {stats.linkedToPeriod} <span className="text-xs font-normal text-gray-500">/ {stats.total} Rejim</span>
                        </p>
                    </div>
                    <div className="p-2.5 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400">
                        <Clock className="w-5 h-5" />
                    </div>
                </div>

                <div className="p-3.5 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Rejimdeki Aktif Personel</p>
                        <p className="text-xl font-bold text-blue-600 dark:text-blue-400 mt-0.5">
                            {stats.totalActiveEmployees}
                        </p>
                    </div>
                    <div className="p-2.5 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                        <Users className="w-5 h-5" />
                    </div>
                </div>

                <div className="p-3.5 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Farklı / Özel Vardiyada Olan</p>
                        <p className="text-xl font-bold text-amber-600 dark:text-amber-400 mt-0.5">
                            {stats.totalDiffSchedules} <span className="text-xs font-normal text-gray-500">Personel</span>
                        </p>
                    </div>
                    <div className="p-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">
                        <RefreshCw className="w-5 h-5" />
                    </div>
                </div>
            </div>

            {message && (
                <div className="bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200 p-3 rounded-lg border border-green-200 dark:border-green-800 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-5 h-5 shrink-0" />
                        <span>{message}</span>
                    </div>
                    <button onClick={() => setMessage(null)} className="text-xs underline ml-4">Kapat</button>
                </div>
            )}
            {error && (
                <div className="bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200 p-3 rounded-lg border border-red-200 dark:border-red-800 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm">
                        <AlertCircle className="w-5 h-5 shrink-0" />
                        <span>{error}</span>
                    </div>
                    <button onClick={() => setError(null)} className="text-xs underline ml-4">Kapat</button>
                </div>
            )}

            <DataTable
                data={workingRegimeList}
                columns={workingRegimeColumns}
                itemsPerPage={10}
                showSearch={true}
                searchPlaceholder="Çalışma rejimi veya çalışma periyodu ara..."
                showPagination={true}
                emptyMessage="Henüz hiçbir çalışma rejimi eklenmemiş."
                isLoading={loading}
                errorMessage={null}
                actionsHeader={
                    <div className="flex items-center space-x-3">
                        {canCreate && (
                            <button
                                onClick={handleAddClick}
                                className="flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg shadow-sm hover:bg-indigo-700 transition-colors"
                            >
                                <PlusCircle className="w-4 h-4 mr-1.5" />
                                Yeni Çalışma Rejimi Ekle
                            </button>
                        )}
                    </div>
                }
            />

            {/* Ekle / Düzenle Modalı */}
            <InfoModal
                isOpen={isModalOpen}
                onClose={handleModalClose}
                title={selectedWorkingRegime ? "Çalışma Rejimini Düzenle" : "Yeni Çalışma Rejimi Ekle"}
                showFooter={false}
                maxWidth="max-w-2xl"
            >
                <WorkingRegimeForm
                    workingRegime={selectedWorkingRegime}
                    workPeriods={workPeriods}
                    onSave={handleFormSubmit}
                    onCancel={handleModalClose}
                    isNew={!selectedWorkingRegime}
                    submitting={loading}
                />
            </InfoModal>

            {/* Vardiyaları Eşitle (Sync) Modalı */}
            <InfoModal
                isOpen={syncModal.isOpen}
                onClose={() => setSyncModal(prev => ({ ...prev, isOpen: false, regime: null }))}
                title="Rejim Personellerinin Vardiyalarını Eşitle"
                showFooter={false}
            >
                {syncModal.regime && (
                    <form onSubmit={handleSyncConfirm} className="space-y-4">
                        <div className="p-3 rounded-lg bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800 text-xs text-indigo-900 dark:text-indigo-200 space-y-1">
                            <div className="font-semibold flex items-center gap-1.5">
                                <CheckCircle2 className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                                {syncModal.regime.name} → {syncModal.regime.defaultWorkPeriodName}
                            </div>
                            <p>
                                Bu çalışma rejiminde <strong>{syncModal.regime.activeEmployeeCount || 0} aktif personel</strong> bulunuyor
                                ({syncModal.regime.matchingScheduleEmployeeCount || 0} uyumlu, {syncModal.regime.differentOrMissingScheduleCount || 0} farklı/atanmamış).
                            </p>
                        </div>

                        <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1.5">
                                <Calendar className="w-3.5 h-3.5 text-indigo-500" />
                                Mevcut Vardiyası Olanlarda Geçerlilik Başlangıç Tarihi:
                            </label>
                            <input
                                type="date"
                                value={syncModal.scheduleEffectiveDate}
                                onChange={(e) => setSyncModal(prev => ({ ...prev, scheduleEffectiveDate: e.target.value }))}
                                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                required
                            />
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                                Not: Hiç vardiya ataması olmayan personellerde doğrudan <strong>İşe Giriş Tarihi</strong> baz alınır. Mevcut vardiyası olanlarda ise eski vardiya bu tarihten 1 gün önce kapatılıp bu tarihten itibaren yeni periyot başlatılır.
                            </p>
                        </div>

                        <label className="flex items-start gap-2.5 p-3 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={syncModal.preserveCustomEmployeeSchedules}
                                onChange={(e) => setSyncModal(prev => ({ ...prev, preserveCustomEmployeeSchedules: e.target.checked }))}
                                className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <div className="text-xs">
                                <span className="font-semibold text-gray-900 dark:text-gray-100">
                                    Sadece vardiyası olmayan personellere ata (Farklı/Özel vardiyası olanlara dokunma)
                                </span>
                                <p className="text-gray-500 dark:text-gray-400 mt-0.5">
                                    İşaretlenmezse, bu rejimdeki tüm aktif personeller seçilen tarihten itibaren <strong>{syncModal.regime.defaultWorkPeriodName}</strong> periyoduna geçirilir.
                                </p>
                            </div>
                        </label>

                        <div className="flex justify-end gap-2 pt-2">
                            <button
                                type="button"
                                onClick={() => setSyncModal(prev => ({ ...prev, isOpen: false, regime: null }))}
                                className="px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300"
                            >
                                İptal
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-4 py-2 text-sm font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1.5"
                            >
                                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                                Vardiyaları Eşitle
                            </button>
                        </div>
                    </form>
                )}
            </InfoModal>

            {/* Personel Aktarım & Güvenli Silme Modalı */}
            <InfoModal
                isOpen={transferModal.isOpen}
                onClose={() => setTransferModal(prev => ({ ...prev, isOpen: false, sourceRegime: null }))}
                title={transferModal.mode === 'delete' ? "Personelleri Başka Rejime Aktar ve Sil" : "Personelleri Başka Çalışma Rejimine Aktar"}
                showFooter={false}
            >
                {transferModal.sourceRegime && (
                    <form onSubmit={handleTransferConfirm} className="space-y-4">
                        <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 text-xs text-amber-900 dark:text-amber-200 flex items-start gap-2.5">
                            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                            <div>
                                <p className="font-semibold">
                                    '{transferModal.sourceRegime.name}' rejimine bağlı {transferModal.sourceRegime.totalEmployeeCount || 0} personel ({transferModal.sourceRegime.activeEmployeeCount || 0} aktif, {transferModal.sourceRegime.inactiveEmployeeCount || 0} pasif) bulunmaktadır.
                                </p>
                                <p className="mt-1">
                                    {transferModal.mode === 'delete'
                                        ? "Bu çalışma rejimini silebilmek için bağlı personelleri aktaracağınız yeni çalışma rejimini seçin."
                                        : "Personellerin aktarılacağı hedef çalışma rejimini seçin."}
                                </p>
                            </div>
                        </div>

                        {otherActiveRegimesForTransfer.length === 0 ? (
                            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs">
                                Sistemde personellerin aktarılabileceği başka aktif bir çalışma rejimi bulunmuyor. Lütfen önce yeni bir aktif çalışma rejimi oluşturun.
                            </div>
                        ) : (
                            <>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                        Hedef Çalışma Rejimi *
                                    </label>
                                    <select
                                        value={transferModal.targetRegimeId}
                                        onChange={(e) => setTransferModal(prev => ({ ...prev, targetRegimeId: e.target.value }))}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                        required
                                    >
                                        {otherActiveRegimesForTransfer.map(r => (
                                            <option key={r.id} value={r.id}>
                                                {r.name} {r.defaultWorkPeriodName ? `(Periyot: ${r.defaultWorkPeriodName})` : '(Manuel Periyot)'}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 space-y-2.5">
                                    <label className="flex items-start gap-2 cursor-pointer text-xs">
                                        <input
                                            type="checkbox"
                                            checked={transferModal.syncSchedulesToTargetRegime}
                                            onChange={(e) => setTransferModal(prev => ({ ...prev, syncSchedulesToTargetRegime: e.target.checked }))}
                                            className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                        />
                                        <div>
                                            <span className="font-semibold text-gray-900 dark:text-gray-100">
                                                Aktarılan personellerin vardiyalarını da hedef rejimin periyoduna geçir
                                            </span>
                                            <p className="text-gray-500 dark:text-gray-400 mt-0.5">
                                                Hedef rejimin varsayılan periyodu varsa, aktarılan personellerin eski vardiyası bir gün önce kapatılarak seçilen tarihten itibaren yeni periyot başlatılır.
                                            </p>
                                        </div>
                                    </label>

                                    {transferModal.syncSchedulesToTargetRegime && (
                                        <div className="pl-6">
                                            <label className="block text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                                Yeni Vardiya Geçerlilik Başlangıç Tarihi:
                                            </label>
                                            <input
                                                type="date"
                                                value={transferModal.scheduleEffectiveDate}
                                                onChange={(e) => setTransferModal(prev => ({ ...prev, scheduleEffectiveDate: e.target.value }))}
                                                className="w-full px-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                            />
                                        </div>
                                    )}
                                </div>
                            </>
                        )}

                        <div className="flex justify-end gap-2 pt-2">
                            <button
                                type="button"
                                onClick={() => setTransferModal(prev => ({ ...prev, isOpen: false, sourceRegime: null }))}
                                className="px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300"
                            >
                                İptal
                            </button>
                            {otherActiveRegimesForTransfer.length > 0 && (
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className={`px-4 py-2 text-sm font-semibold rounded-lg text-white flex items-center gap-1.5 ${
                                        transferModal.mode === 'delete'
                                            ? 'bg-red-600 hover:bg-red-700'
                                            : 'bg-indigo-600 hover:bg-indigo-700'
                                    }`}
                                >
                                    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                                    {transferModal.mode === 'delete' ? 'Personelleri Aktar ve Rejimi Sil' : 'Personelleri Aktar'}
                                </button>
                            )}
                        </div>
                    </form>
                )}
            </InfoModal>

            {/* 0 Personelli Rejim Silme Onayı */}
            <InfoModal
                isOpen={isConfirmModalOpen}
                onClose={handleModalClose}
                title="Silme Onayı"
                message={`'${workingRegimeToDelete?.name || ''}' çalışma rejimini silmek istediğinizden emin misiniz?`}
                type="confirm"
                onConfirm={handleDeleteConfirm}
                confirmText="Evet, Sil"
                cancelText="Hayır"
            />
        </div>
    );
}
